import type { QueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, createRoute, Outlet, redirect } from '@tanstack/react-router'
import { type ComponentType, Suspense } from 'react'
import { PendingPage } from '../components/StatusPage/StatusPage'
import { Toaster } from '../components/ui/toast'
import { TooltipProvider } from '../components/ui/tooltip'
import { type Permit, permit } from '../lib/permit'
import { cached } from '../query'
import { type SessionConfig, SessionProvider, type Viewer } from '../session/useSession'
import { ThemeProvider } from '../session/useTheme'

export type RouterContext = { queryClient: QueryClient }

export class Refused extends Error {
  readonly status = 403

  constructor(readonly slice: string) {
    super(slice)
    this.name = 'Refused'
  }
}

export function rootRoutesFor<V extends Viewer>({
  session,
  layout: Layout,
  signInPath = '/sign-in',
}: {
  session: SessionConfig<V>
  layout: ComponentType<{ children: React.ReactNode }>
  signInPath?: string
}) {
  const rootRoute = createRootRouteWithContext<RouterContext>()({
    component: () => (
      <ThemeProvider>
        <TooltipProvider delay={300}>
          <Toaster>
            <Suspense fallback={<PendingPage />}>
              <SessionProvider config={session}>
                <Outlet />
              </SessionProvider>
            </Suspense>
          </Toaster>
        </TooltipProvider>
      </ThemeProvider>
    ),
  })

  const signedInRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'signedIn',
    beforeLoad: async ({ context }) => {
      if ((await context.queryClient.query(cached(session.profileQuery))) == null) {
        throw redirect({ to: signInPath, replace: true })
      }
    },
    component: () => (
      <Layout>
        <Outlet />
      </Layout>
    ),
  })

  const permitOf = async (context: RouterContext) => {
    const profile: V | null = await context.queryClient.query(cached(session.profileQuery))

    if (profile == null) throw redirect({ to: signInPath, replace: true })

    return permit(profile.permissions)
  }

  const guardedRoute =
    (allows: (held: Permit, slice: string) => boolean) =>
    (slice: string) =>
    async ({ context }: { context: RouterContext }) => {
      if (!allows(await permitOf(context), slice)) throw new Refused(slice)
    }

  const permittedRoute = guardedRoute((held, slice) => held.reads(slice))

  const writesRoute = guardedRoute((held, slice) => held.writes(slice) && held.reads(slice))

  return { rootRoute, signedInRoute, permittedRoute, writesRoute }
}
